function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback, useLayoutEffect, useRef, useState, Suspense } from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import { throttle } from 'lodash';
import { plusIcon16 } from '@lululemon/ecom-pattern-library-icons';
import useResizeObserver from '@react-hook/resize-observer';
import { useInView } from 'react-intersection-observer';
import useMediaQuery from '../../utils/hooks/use-media-query';
import LinkGenerator from '../link-generator';
import { convertAsterisk } from '../../utils/helpers';
import ColorSwatch from '../color-swatch';
import CarouselButton from '../carousel/common/carousel-button';
import Icon from '../icons';
require("./product-tile-swatches.css");
var styles = {
  "swatchGroup": "swatchGroup-2RJmx",
  "swatchLink": "swatchLink-4gzGR",
  "swatchListItem": "swatchListItem-1W3Ad",
  "swatchGroupCarousel": "swatchGroupCarousel-2r-d4",
  "swatch": "swatch-2rWum",
  "moreSwatchesButton": "moreSwatchesButton-3pLhL",
  "moreSwatchesIcon": "moreSwatchesIcon-2Zz38",
  "carouselContainer": "carouselContainer-2WWsZ",
  "carouselFlexWrapper": "carouselFlexWrapper-USGOy",
  "carouselNoArrows": "carouselNoArrows-1j9OK",
  "carouselButton": "carouselButton-3opUr",
  "carouselButtonDisabled": "carouselButtonDisabled-2ix8V",
  "carouselButtonLeft": "carouselButtonLeft-1Q2_g",
  "carouselButtonRight": "carouselButtonRight-3QqnX"
};
/**
 * Breakpoints optimized to fit color swatches within product tiles with minimum 7px gutter
 */
var MQ_LIST = {
  xl: '(min-width: 1328px)',
  l: '(min-width: 1188px)',
  m: '(min-width: 1048px)',
  s: '(min-width: 992px)',
  xs: ''
};

// should slides per page be based on screen size or `carousel container` size?
var SLIDES_PER_PAGE_MQ_LIST = {
  xl: 7,
  l: 6,
  m: 5,
  s: 4,
  xs: 5
};
var SWATCH_DESKTOP_WIDTH = 37;
var RESIZE_THROTTLE_TIME = 500;
var Carousel = /*#__PURE__*/React.lazy(() => import('../carousel'));
var SwatchCarousel = _ref => {
  var {
    children,
    selectedIndex,
    slidesPerPage
  } = _ref;
  var [slideIndex, setSlideIndex] = useState(selectedIndex);
  var leftArrowDisabled = slideIndex === 0;
  var rightArrowDisabled = slideIndex >= children.length - slidesPerPage;
  var showCarouselArrows = children.length > slidesPerPage;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.carouselContainer
  }, showCarouselArrows && /*#__PURE__*/React.createElement(CarouselButton, {
    className: cs(styles.carouselButton, styles.carouselButtonLeft, {
      [styles.carouselButtonDisabled]: leftArrowDisabled
    }),
    disabled: leftArrowDisabled,
    direction: "left",
    onClick: () => {
      setSlideIndex(Math.max(slideIndex - slidesPerPage, 0));
    },
    disableKeyboardNavigation: true
  }), /*#__PURE__*/React.createElement("div", {
    className: cs(styles.carouselFlexWrapper, {
      [styles.carouselNoArrows]: !showCarouselArrows
    }),
    "data-testid": "carousel",
    "data-slideindex": slideIndex
  }, /*#__PURE__*/React.createElement(Carousel, {
    slidesPerPage: slidesPerPage,
    slidesPerScroll: slidesPerPage,
    slideIndex: slideIndex,
    onSlideIndexChange: setSlideIndex
  }, children)), showCarouselArrows && /*#__PURE__*/React.createElement(CarouselButton, {
    className: cs(styles.carouselButton, styles.carouselButtonRight, {
      [styles.carouselButtonDisabled]: rightArrowDisabled
    }),
    disabled: rightArrowDisabled,
    direction: "right",
    onClick: () => {
      setSlideIndex(Math.min(slideIndex + slidesPerPage, children.length - slidesPerPage));
    },
    disableKeyboardNavigation: true
  }));
};
SwatchCarousel.propTypes = {
  children: PropTypes.node.isRequired,
  selectedIndex: PropTypes.number.isRequired,
  slidesPerPage: PropTypes.number.isRequired
};
var MoreSwatchesButton = _ref2 => {
  var {
    pdpLinkAnalyticsAttributes,
    name,
    href
  } = _ref2;
  return /*#__PURE__*/React.createElement(LinkGenerator, _extends({}, pdpLinkAnalyticsAttributes, {
    "aria-label": "View more color options of ".concat(convertAsterisk(name.trim(), '')),
    className: styles.moreSwatchesButton,
    href: href
  }), /*#__PURE__*/React.createElement(Icon, {
    content: plusIcon16,
    className: styles.moreSwatchesIcon,
    "aria-hidden": "true"
  }));
};
MoreSwatchesButton.propTypes = {
  pdpLinkAnalyticsAttributes: PropTypes.shape({}).isRequired,
  href: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
};
var SwatchLink = _ref3 => {
  var {
    analyticsAttributes,
    href,
    onSelect,
    onSwatchClick,
    isSelected,
    swatch
  } = _ref3;
  var [path, search] = href.split('?');
  var params = new URLSearchParams(search);
  params.set('color', swatch.id);
  return /*#__PURE__*/React.createElement(LinkGenerator, _extends({}, analyticsAttributes, {
    className: styles.swatchLink,
    href: "".concat(path, "?").concat(params),
    onClick: e => {
      onSwatchClick({
        event: e,
        swatch
      });
    },
    onFocus: onSelect,
    onMouseEnter: onSelect,
    onTouchStart: onSelect,
    rel: "nofollow"
  }), /*#__PURE__*/React.createElement(ColorSwatch, {
    id: swatch.id,
    name: swatch.name,
    className: styles.swatch,
    isDisabled: swatch.isDisabled,
    isSelected: isSelected
  }));
};
SwatchLink.propTypes = {
  analyticsAttributes: PropTypes.shape({}).isRequired,
  href: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSwatchClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  swatch: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    isDisabled: PropTypes.bool
  }).isRequired
};
var ProductTileSwatches = _ref4 => {
  var {
    analyticsAttributes,
    pdpLinkAnalyticsAttributes,
    href,
    lazy,
    name,
    onSelect,
    onSwatchClick,
    selectedIndex,
    swatches
  } = _ref4;
  var [carouselWidth, setCarouselWidth] = useState(null);
  var [carouselSlidesPerPage, setCarouselSlidesPerPage] = useState(null);
  var ref = useRef(null);
  var {
    ref: inViewRef,
    inView
  } = useInView({
    triggerOnce: true,
    rootMargin: '300px'
  });
  var setRefs = useCallback(node => {
    inViewRef(node);
    ref.current = node;
    var width = !!ref.current ? ref.current.getBoundingClientRect().width : null;
    setCarouselWidth(width);
  }, [inViewRef]);
  /*
    Resize Observer and `desktopSlidesPerPageCalc` are needed to protect against swatche slides being compressed inside carousel on smaller container sizes (ie: product tiles in 4-column grids) DAISY-940
    Assessment needed to determine better solution for displaying swatches inside ProductTiles
  */
  useResizeObserver(ref, throttle(entry => {
    var width = !!entry.contentRect ? entry.contentRect.width : null;
    setCarouselWidth(width);
  }, RESIZE_THROTTLE_TIME));
  useLayoutEffect(() => {
    var desktopSlidesPerPageCalc = function () {
      var carouselWidth = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      return carouselWidth ? Math.floor(carouselWidth / SWATCH_DESKTOP_WIDTH) : SLIDES_PER_PAGE_MQ_LIST[mq];
    };
    var slidesPerPage = desktopSlidesPerPageCalc(carouselWidth);
    setCarouselSlidesPerPage(slidesPerPage);
  }, [carouselWidth, mq]);
  var mq = useMediaQuery(MQ_LIST);
  var isDesktop = useMediaQuery(MQ_LIST) !== 'xs';
  var slidesPerPage = isDesktop && carouselWidth ? carouselSlidesPerPage : SLIDES_PER_PAGE_MQ_LIST[mq];
  var truncatedSwatches = isDesktop ? swatches : swatches.slice(0, slidesPerPage);
  var displaySwatches = inView || !lazy;
  var swatchComponents = displaySwatches && truncatedSwatches.map((swatch, index) => /*#__PURE__*/React.createElement(SwatchLink
  // eslint-disable-next-line react/no-array-index-key
  , {
    key: "".concat(swatch.id, "-").concat(index),
    analyticsAttributes: analyticsAttributes,
    href: href,
    swatch: swatch,
    isSelected: selectedIndex === index,
    onSwatchClick: onSwatchClick,
    onSelect: () => {
      onSelect(index);
    }
  }));
  return /*#__PURE__*/React.createElement("div", {
    className: cs(styles.swatchGroup, {
      [styles.swatchGroupCarousel]: isDesktop
    }),
    ref: setRefs,
    "data-testid": isDesktop ? 'swatches-desktop' : 'swatches-mobile',
    role: !isDesktop ? 'list' : undefined
  }, displaySwatches && /*#__PURE__*/React.createElement(React.Fragment, null, isDesktop ? /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement("div", null)
  }, /*#__PURE__*/React.createElement(SwatchCarousel, {
    selectedIndex: selectedIndex,
    slidesPerPage: slidesPerPage
  }, swatchComponents)) : /*#__PURE__*/React.createElement(React.Fragment, null, swatchComponents.map((swatchLink, index) => /*#__PURE__*/React.createElement("div", {
    role: "listitem",
    className: styles.swatchListItem
    // eslint-disable-next-line react/no-array-index-key
    ,
    key: "swatchlist-".concat(index)
  }, swatchLink))), !isDesktop && truncatedSwatches.length < swatches.length && /*#__PURE__*/React.createElement(MoreSwatchesButton, {
    pdpLinkAnalyticsAttributes: pdpLinkAnalyticsAttributes,
    href: href,
    name: name
  })));
};
ProductTileSwatches.propTypes = {
  /** Defines HTML attributes for link tracking for swatch links (e.g., { "data-lulu-track": "pdp-link" }) */
  analyticsAttributes: PropTypes.shape({}),
  /** Product tile analytics attributes, automatically provided when used inside of `ProductTile` */
  pdpLinkAnalyticsAttributes: PropTypes.shape({}),
  /** Product URL for links, automatically provided when used inside of `ProductTile` */
  href: PropTypes.string,
  /** Whether to lazyload the swatches using intersection observer. */
  lazy: PropTypes.bool,
  /** Product name, automatically provided when used inside of `ProductTile` */
  name: PropTypes.string,
  /** Callback fired when hovering or focusing on a swatch, called with selected swatch index */
  onSelect: PropTypes.func.isRequired,
  onSwatchClick: PropTypes.func,
  /** The currently selected swatch index */
  selectedIndex: PropTypes.number.isRequired,
  swatches: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool
  })).isRequired
};
ProductTileSwatches.defaultProps = {
  analyticsAttributes: {},
  pdpLinkAnalyticsAttributes: {},
  href: '',
  lazy: true,
  name: '',
  onSwatchClick: () => {}
};
export default ProductTileSwatches;